/**
 * Item 294 (Phase B core) — a group's critical alerts: on/off and the spray-sound threshold (10–99 %,
 * default 40). Superusers only (the Groups page shows the button to superusers and the server refuses
 * everyone else). The settings live on the group, so cameras added to it later follow them.
 *
 * Turning it on also switches on the Graffiti sound (Aerosol_spray) on the group's cameras (item 280's
 * code, on the server), so while it is on the dialog shows the server's dry run first — "N cameras will
 * get the Graffiti sound switched on" — and Save repeats the same PUT for real. Turning it off leaves
 * the cameras' sound settings as they are. Modelled on GroupAudioDialog.
 *
 * Item 406 (design §6.5) — it grows into a two-part sheet: the critical part above, as it was, and the Suspicious part: a
 * switch per group (off = shadow mode: levels are computed and stored, nothing is shown or pushed), one Off / Weak /
 * Strong row per clue (the deferred ones locked), the bars and parameters under an Advanced fold. "Preview last 30 days"
 * replays the candidate rules as a dry run, only when asked (dry runs count against the 60/hour throttle). Save sends
 * `level_rules` only when they changed (absent = keep), always whole and normalised; the server validates them, and its
 * refusal is read out in the sheet.
 *
 * Item 408 — the five appearance clues can be weighed, each tagged Beta (they only run on a camera with its Beta switch on).
 * "Use beta test rules" fills the form with the owner's test rules and the Critical switch on, since clue points reach
 * Critical only with that switch on; it saves nothing, Save does.
 */
import * as Dialog from '@radix-ui/react-dialog'
import * as Switch from '@radix-ui/react-switch'
import { useMemo, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Lock, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import {
  APPEARANCE_CLUES, CLUE_WEIGHTS, LEVEL_RULE_FIELDS, LOCKED_CLUES, SCORING_CLUES, normaliseLevelRules, parseRuleNumber, readRuleField,
  withBetaTestRules, withRuleField,
} from '@/lib/levelRules'
import { toast } from '@/lib/toast'
import { cn, formatCardDateTime } from '@/lib/utils'
import type { AppearanceClueCode, CameraGroup, ClueWeight, LevelPreview, LevelRules, LockedClueCode, ScoringClueCode } from '@/types'

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** "40" -> 0.4; anything that is not a whole number from 10 to 99 -> null. */
function percentToFraction(text: string): number | null {
  if (!/^\d{2}$/.test(text.trim())) return null
  const n = Number(text)
  return n >= 10 && n <= 99 ? n / 100 : null
}

/** The server's reason for a 400: {"detail": "..."} or a field error {"spray_threshold": ["..."]}. */
function serverMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data
  if (!data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  if (typeof d.detail === 'string' && d.detail) return d.detail
  for (const field of ['spray_threshold', 'enabled', 'level_rules']) {
    const v = d[field]
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0]
  }
  return null
}

/** #406 — everything the server refused about the rules (it names each field), else its one reason, else nothing. */
function rulesProblems(error: unknown): string[] {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data
  const rules = data && typeof data === 'object' ? (data as Record<string, unknown>).level_rules : undefined
  if (Array.isArray(rules)) return rules.filter((v): v is string => typeof v === 'string')
  if (typeof rules === 'string') return [rules]
  return []
}

/** #406 — one clue's Off / Weak / Strong. A radio group by hand so it can be a segmented row: one Tab stop, the arrow
 *  keys move the choice. Locked: shown, not moved. */
function WeightControl({ code, value, locked, disabled, onChange }: {
  code: ScoringClueCode | AppearanceClueCode | LockedClueCode
  value: ClueWeight
  locked?: boolean
  disabled?: boolean
  onChange?: (weight: ClueWeight) => void
}) {
  const { t } = useTranslation('groups')
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step || locked || disabled) return
    e.preventDefault()
    const next = (CLUE_WEIGHTS.indexOf(value) + step + CLUE_WEIGHTS.length) % CLUE_WEIGHTS.length
    onChange?.(CLUE_WEIGHTS[next])
    const buttons = e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons[next]?.focus()
  }
  return (
    <div
      role="radiogroup"
      aria-label={t(`suspicious.clues.${code}`)}
      onKeyDown={onKeyDown}
      className="flex shrink-0 overflow-hidden rounded-md border border-border"
    >
      {CLUE_WEIGHTS.map((weight) => (
        <button
          key={weight}
          type="button"
          role="radio"
          aria-checked={value === weight}
          tabIndex={value === weight ? 0 : -1}
          disabled={locked || disabled}
          onClick={() => onChange?.(weight)}
          className={cn(
            'h-11 min-w-[4rem] flex-1 px-3 text-xs font-medium transition-colors sm:h-8',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-60',
            value === weight ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {t(`suspicious.weights.${weight}`)}
        </button>
      ))}
    </div>
  )
}

/** #406 — the answer of the 30-day replay (design §5.5): honest about the events it cannot count. */
function LevelPreviewResult({ preview }: { preview: LevelPreview }) {
  const { t } = useTranslation('groups')
  if (preview.total_events === 0) return <p className="text-muted-foreground">{t('suspicious.previewEmpty')}</p>
  const th = 'px-2 py-1 text-left font-medium text-muted-foreground'
  const td = 'px-2 py-1 tabular-nums'
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{t('suspicious.previewThresholds')}</p>
      {preview.events_without_clues > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('suspicious.previewNoClues', { count: preview.events_without_clues, total: preview.total_events })}
        </p>
      )}
      {preview.sampled ? <p className="text-xs text-muted-foreground">{t('suspicious.previewSampled', { every: preview.sampled })}</p> : null}
      <div className="max-h-48 overflow-auto">
        <table aria-label={t('suspicious.previewDay')} className="w-full text-xs">
          <thead>
            <tr>
              <th scope="col" className={th}>{t('suspicious.previewDay')}</th>
              <th scope="col" className={th}>{t('suspicious.previewSuspicious')}</th>
              <th scope="col" className={th}>{t('suspicious.previewCritical')}</th>
              <th scope="col" className={th}>{t('suspicious.previewRecent')}</th>
            </tr>
          </thead>
          <tbody>
            {preview.days.map((day) => (
              <tr key={day.date} className="border-t border-border/60">
                <td className={td}>{day.date}</td>
                <td className={td}>{day.suspicious}</td>
                <td className={td}>{day.critical}</td>
                <td className={td}>{day.recent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.cameras.length > 0 && (
        <div className="max-h-48 overflow-auto">
          <table aria-label={t('suspicious.previewCamera')} className="w-full text-xs">
            <thead>
              <tr>
                <th scope="col" className={th}>{t('suspicious.previewCamera')}</th>
                <th scope="col" className={th}>{t('suspicious.previewSuspicious')}</th>
                <th scope="col" className={th}>{t('suspicious.previewCritical')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.cameras.map((camera) => (
                <tr key={camera.camera_id} className="border-t border-border/60">
                  <td className={cn(td, 'break-words')}>{camera.name}</td>
                  <td className={td}>{camera.suspicious}</td>
                  <td className={td}>{camera.critical}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const asPercent = (fraction: number) => Math.round(fraction * 100)

export function GroupCriticalDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  // The user's choices; null = still what the server has.
  const [enabledChoice, setEnabledChoice] = useState<boolean | null>(null)
  const [percent, setPercent] = useState<string | null>(null)
  // #406 — the rules as changed here (null = still what the server has), the number boxes as typed, and which candidate
  // the last 30-day preview was asked for (a preview of other rules, or of the other Critical switch, is not shown as this one's).
  const [rulesChoice, setRulesChoice] = useState<LevelRules | null>(null)
  const [numberText, setNumberText] = useState<Record<string, string>>({})
  const [previewedRules, setPreviewedRules] = useState<string | null>(null)

  const settings = useQuery({
    queryKey: queryKeys.cameraGroups.critical(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.getCriticalSettings(group.id, signal),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const loaded = settings.data
  const enabled = enabledChoice ?? loaded?.enabled ?? false
  const percentText = percent ?? (loaded ? String(asPercent(loaded.spray_threshold)) : '')
  const fraction = percentToFraction(percentText)
  const percentInvalid = !!loaded && fraction === null

  // #406 — what would be saved: the server's rules over the defaults, the changes, then the number boxes as typed.
  const loadedRules = useMemo(() => normaliseLevelRules(loaded?.level_rules), [loaded])
  const { rules, numbersInvalid } = useMemo(() => {
    let out = rulesChoice ?? loadedRules
    let invalid = false
    for (const field of LEVEL_RULE_FIELDS) {
      const text = numberText[field.key]
      if (text === undefined) continue
      const n = parseRuleNumber(text)
      if (n === null) invalid = true
      else out = withRuleField(out, field.path, n)
    }
    return { rules: out, numbersInvalid: invalid }
  }, [rulesChoice, numberText, loadedRules])
  const rulesJson = JSON.stringify(rules)
  const rulesChanged = !!loaded && rulesJson !== JSON.stringify(loadedRules)

  const percentChanged = !!loaded && fraction !== null && asPercent(fraction) !== asPercent(loaded.spray_threshold)
  const changed = !!loaded && (enabled !== loaded.enabled || rulesChanged || percentChanged)

  // While on: which cameras turning on would give the Graffiti sound — a dry run, nothing written. The
  // count does not depend on the threshold, so it is not sent: one dry run per opening / switching on
  // (dry runs count against the 60/hour throttle; one per threshold value could lock Save out).
  const preview = useQuery({
    queryKey: queryKeys.cameraGroups.criticalPreview(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.setCriticalSettings(group.id, { enabled: true }, true, signal),
    enabled: open && !!loaded && enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  // #406 — a Suspicious edit never alters Critical. The field shows whole percents and the API stores any float, so
  // sending the field back unchanged would move a stored 0.425 to 0.43: with new rules and no new percent the threshold
  // stays out of the body (absent = keep). A save without new rules is as it always was.
  const keepThreshold = rulesChanged && !percentChanged
  const save = useMutation({
    mutationFn: () => cameraGroupsApi.setCriticalSettings(
      group.id,
      {
        enabled,
        ...(keepThreshold ? {} : { spray_threshold: fraction! }),
        ...(rulesChanged ? { level_rules: rules } : {}),
      },
      false,
    ),
    onSuccess: () => {
      toast.success(t('critical.saved', { name: group.name }))
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.critical(group.id) })
      // Turning on switched the Graffiti sound on for cameras: their lists and Sound settings refresh.
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: ['camera-audio-classes'] })
      onOpenChange(false)
    },
    onError: (error) => {
      const reason = serverMessage(error)
      if (reason) toast.error(t('critical.failed'), reason)
      else toast.error(t('critical.failed'))
    },
  })

  // #406 — the 30-day replay of the candidate rules: a dry run, only when the button asks for it
  const levelPreview = useQuery({
    queryKey: queryKeys.cameraGroups.levelPreview(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.setCriticalSettings(group.id, { enabled, level_rules: rules }, true, signal),
    enabled: false,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const candidate = `${enabled}:${rulesJson}`
  const askPreview = () => {
    setPreviewedRules(candidate)
    void levelPreview.refetch()
  }
  const previewShown = previewedRules === candidate
  const previewProblems = previewShown && levelPreview.isError
    ? rulesProblems(levelPreview.error).length > 0
      ? rulesProblems(levelPreview.error)
      : [serverMessage(levelPreview.error) ?? t('suspicious.previewFailed')]
    : []
  const previewAnswer = previewShown && !levelPreview.isFetching && !levelPreview.isError ? levelPreview.data?.preview : undefined
  const saveProblems = rulesProblems(save.error)
  // A change makes the server's last refusal old news
  const changeRules = (next: LevelRules) => {
    setRulesChoice(next)
    if (save.isError) save.reset()
  }
  // #408 — the owner's test rules into the form, over what is on screen (typed numbers included), and the Critical switch
  // on: two clues are meant to raise Critical. The boxes then read from the rules. Nothing is sent: the superuser still
  // presses Save.
  const fillBetaRules = () => {
    changeRules(withBetaTestRules(rules))
    setNumberText({})
    setEnabledChoice(true)
  }
  const changeNumber = (key: string, text: string) => {
    setNumberText((old) => ({ ...old, [key]: text }))
    if (save.isError) save.reset()
  }

  const counts = preview.data?.audio
  const previewReady = preview.isSuccess && !preview.isFetching
  // On: the preview must have answered; saving is useful when something changes, or when cameras added
  // since still need the Graffiti sound. Off: only when that is a change.
  const canSave = !!loaded && !percentInvalid && !numbersInvalid && !save.isPending
    && (enabled ? previewReady && (changed || (counts?.updated ?? 0) > 0) : changed)

  return (
    <Dialog.Root open={open} onOpenChange={save.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">{t('critical.title', { name: group.name })}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('critical.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={save.isPending}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {settings.isError ? (
            <p className="mt-4 text-sm text-destructive">{t('critical.loadFailed')}</p>
          ) : !loaded ? (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('critical.loading')}
            </p>
          ) : (
            <>
              <div className="mt-4 flex items-center justify-between gap-3">
                <label className="text-sm font-medium" htmlFor="group-critical-enabled">{t('critical.enabledLabel')}</label>
                <Switch.Root
                  id="group-critical-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabledChoice}
                  disabled={save.isPending}
                  className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=unchecked]:bg-input disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Switch.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
                </Switch.Root>
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium" htmlFor="group-critical-threshold">{t('critical.thresholdLabel')}</label>
                <Input
                  id="group-critical-threshold"
                  type="number"
                  inputMode="numeric"
                  min={10}
                  max={99}
                  step={1}
                  value={percentText}
                  onChange={(e) => setPercent(e.target.value)}
                  disabled={save.isPending}
                  aria-invalid={percentInvalid || undefined}
                  className="h-11 sm:h-10"
                />
                <p className="text-xs text-muted-foreground">{t('critical.thresholdHint')}</p>
              </div>

              {loaded.updated_at && loaded.updated_by && (
                <p className="mt-3 text-xs text-muted-foreground">
                  {t('critical.lastChanged', { name: loaded.updated_by, time: formatCardDateTime(loaded.updated_at) })}
                </p>
              )}

              <div className="mt-4 min-h-[3.5rem] rounded-lg bg-muted/50 p-3 text-sm" aria-live="polite">
                {percentInvalid ? (
                  <p className="text-destructive">{t('critical.thresholdRange')}</p>
                ) : !enabled ? (
                  <p className="text-muted-foreground">{t('critical.offNote')}</p>
                ) : preview.isError ? (
                  <p className="text-destructive">{serverMessage(preview.error) ?? t('critical.previewFailed')}</p>
                ) : !counts || preview.isFetching ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />{t('critical.checking')}
                  </p>
                ) : (
                  <p className="font-medium">{t('critical.willSwitchOn', { count: counts.updated })}</p>
                )}
              </div>

              {/* #406 — the Suspicious part of the sheet */}
              <section aria-labelledby="group-suspicious-heading" className="mt-6 border-t border-border pt-4">
                <h3 id="group-suspicious-heading" className="text-sm font-semibold">{t('suspicious.heading')}</h3>

                <div className="mt-1 flex min-h-11 items-center justify-between gap-3">
                  <label className="text-sm font-medium" htmlFor="group-suspicious-enabled">{t('suspicious.enabledLabel')}</label>
                  <Switch.Root
                    id="group-suspicious-enabled"
                    checked={rules.suspicious_enabled}
                    onCheckedChange={(on) => changeRules({ ...(rulesChoice ?? loadedRules), suspicious_enabled: on })}
                    disabled={save.isPending}
                    className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors after:absolute after:-inset-3 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=unchecked]:bg-input disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Switch.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
                  </Switch.Root>
                </div>
                <p className="text-xs text-muted-foreground">{t('suspicious.enabledHint')}</p>

                <p className="mt-3 text-xs text-muted-foreground">{t('suspicious.cluesLegend')}</p>
                <div className="mt-2 space-y-2">
                  {SCORING_CLUES.filter((code) => code !== 'at_train').map((code) => (
                    <div key={code} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <span className="text-sm">{t(`suspicious.clues.${code}`)}</span>
                      <WeightControl
                        code={code}
                        value={rules.clues[code] ?? 'off'}
                        disabled={save.isPending}
                        onChange={(weight) => {
                          const base = rulesChoice ?? loadedRules
                          changeRules({ ...base, clues: { ...base.clues, [code]: weight } })
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-3 space-y-2">
                  {APPEARANCE_CLUES.map((code) => (
                    <div key={code} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <span className="flex items-center gap-1.5 text-sm">
                        {t(`suspicious.clues.${code}`)}
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-amber-700 dark:text-amber-400">
                          {t('suspicious.betaTag')}
                        </span>
                      </span>
                      <WeightControl
                        code={code}
                        value={rules.clues[code] ?? 'off'}
                        disabled={save.isPending}
                        onChange={(weight) => {
                          const base = rulesChoice ?? loadedRules
                          changeRules({ ...base, clues: { ...base.clues, [code]: weight } })
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <Button type="button" variant="outline" className="h-11 w-full shrink-0 sm:h-9 sm:w-auto" onClick={fillBetaRules} disabled={save.isPending}>
                    {t('suspicious.betaRules')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('suspicious.betaRulesNote')}</p>
                </div>

                <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lock className="h-3 w-3 shrink-0" aria-hidden />{t('suspicious.locked')}
                </p>
                <div className="mt-2 space-y-2">
                  {LOCKED_CLUES.map((code) => (
                    <div key={code} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <span className="text-sm text-muted-foreground">{t(`suspicious.clues.${code}`)}</span>
                      <WeightControl code={code} value={rules.clues[code] ?? 'off'} locked />
                    </div>
                  ))}
                </div>

                <details className="mt-3">
                  <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium sm:min-h-0 sm:py-1">{t('suspicious.advanced')}</summary>
                  <div className="mt-2 space-y-3">
                    {LEVEL_RULE_FIELDS.filter((field) => field.key !== 'atTrainFrames').map((field) => {
                      const id = `group-suspicious-${field.key}`
                      const text = numberText[field.key] ?? String(readRuleField(rules, field.path) ?? '')
                      return (
                        <div key={field.key} className="space-y-1">
                          <label className="text-xs font-medium" htmlFor={id}>{t(`suspicious.fields.${field.key}`)}</label>
                          <Input
                            id={id}
                            type="number"
                            inputMode={field.step < 1 ? 'decimal' : 'numeric'}
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            value={text}
                            onChange={(e) => changeNumber(field.key, e.target.value)}
                            disabled={save.isPending}
                            aria-invalid={parseRuleNumber(text) === null || undefined}
                            className="h-11 sm:h-10"
                          />
                        </div>
                      )
                    })}
                  </div>
                </details>

                <Button
                  type="button"
                  variant="outline"
                  className="mt-3 h-11 w-full sm:h-9 sm:w-auto"
                  onClick={askPreview}
                  disabled={numbersInvalid || save.isPending || levelPreview.isFetching}
                >
                  {t('suspicious.preview')}
                </Button>

                <div data-rules-status aria-live="polite" className="mt-3 space-y-2 text-sm empty:hidden">
                  {numbersInvalid && <p className="text-destructive">{t('suspicious.numberInvalid')}</p>}
                  {[...saveProblems, ...previewProblems].map((problem) => (
                    <p key={problem} className="text-destructive">{problem}</p>
                  ))}
                  {previewShown && levelPreview.isFetching && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />{t('suspicious.previewChecking')}
                    </p>
                  )}
                  {previewAnswer && <LevelPreviewResult preview={previewAnswer} />}
                </div>
              </section>
            </>
          )}

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="h-11 flex-1 sm:h-10" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button className="h-11 flex-1 sm:h-10" onClick={() => save.mutate()} disabled={!canSave}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('critical.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
